import { ZillowInsuranceServicesLogo } from '@zillow/zrs-comet';
import { DetailedIconPaintRoller } from '@zillow/constellation';
import { Box, Image, Layout, Modal, Paragraph, TextButton } from '@zillow/constellation-10';

import ModalFooter from '../ModalFooter';
import RentalProtectionWhatsCoveredSection from '../../rentGuarantee/ProductIntroIllustrativeV2/components/RentalProtectionWhatsCoveredSection';
import { PRODUCT_INTRO_TYPES } from '../../../containers/RentGuaranteePage/constants';
import MissedRentIcon from '../../../images/rentalProtection/missed-rent.svg';
import PropertyDamageIcon from '../../../images/rentalProtection/property-damage.svg';
import PetDamageIcon from '../../../images/rentalProtection/pet-damage.svg';
import ExcessCleaningIcon from '../../../images/rentalProtection/excess-cleaning.svg';
import EvictionCostsIcon from '../../../images/rentalProtection/eviction-costs.svg';

type Props = {
  onClose: () => void;
  onSubmit: () => void;
  coverageSummaryUrl: string;
};

const RentalProtectionWhatsCoveredModal = ({ onClose, onSubmit, coverageSummaryUrl }: Props) => {
  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && onClose()}
      header={<ZillowInsuranceServicesLogo />}
      footer={<ModalFooter submitText="Ok" onSubmit={onSubmit} />}
      size={{ base: 'fullScreen', lg: 'lg' }}
      body={
        <div data-testid="RentalProtectionWhatsCoveredModal">
          <Box css={{ lgDown: { display: 'none' } }}>
            <RentalProtectionWhatsCoveredSection
              productIntroType={PRODUCT_INTRO_TYPES.SOH_ILLUSTRATIVE}
              isInModal={true}
            />
            <Paragraph css={{ marginBlock: '600' }}>
              For more information, view a{' '}
              <TextButton asChild>
                <a target="_blank" rel="noopener noreferrer" href={coverageSummaryUrl}>
                  sample coverage summary
                </a>
              </TextButton>
              .
            </Paragraph>
          </Box>
          <Box css={{ lg: { display: 'none' } }}>
            <Paragraph css={{ textStyle: 'heading-sm', marginBottom: '200', fontFamily: 'heading-alt' }}>
              What&apos;s covered
            </Paragraph>
            <Paragraph css={{ marginBottom: '400' }}>Examples of potential covered events:</Paragraph>
            <Box css={{ marginTop: '600' }}>
              <Layout.Root>
                <Layout.Content css={{ gridColumnEnd: 'span 2', padding: '0', marginBottom: '200' }}>
                  <Image src={MissedRentIcon} alt="dollar sign" css={{ width: '44px', height: '44px' }} />
                </Layout.Content>
                <Layout.Content css={{ gridColumnEnd: 'span 10', padding: '0', marginBottom: '200' }}>
                  <Paragraph textStyle="body-bold">Missed Rent</Paragraph>
                  <Paragraph textStyle="body-sm" color="text.subtle">
                    If your renter falls on tough times, coverage may help recoup lost rent.
                  </Paragraph>
                </Layout.Content>
                <Layout.Content css={{ gridColumnEnd: 'span 2', padding: '0', marginBottom: '200' }}>
                  <Image src={PropertyDamageIcon} alt="broken property" css={{ width: '44px', height: '44px' }} />
                </Layout.Content>
                <Layout.Content css={{ gridColumnEnd: 'span 10', padding: '0', marginBottom: '200' }}>
                  <Paragraph textStyle="body-bold">Property damage</Paragraph>
                  <Paragraph textStyle="body-sm" color="text.subtle">
                    Claim the cost for damage to carpet, walls, floors, fixtures, and appliances.
                  </Paragraph>
                </Layout.Content>
                <Layout.Content css={{ gridColumnEnd: 'span 2', padding: '0', marginBottom: '200' }}>
                  <Image src={PetDamageIcon} alt="pet paw" css={{ width: '44px', height: '44px' }} />
                </Layout.Content>
                <Layout.Content css={{ gridColumnEnd: 'span 10', padding: '0', marginBottom: '200' }}>
                  <Paragraph textStyle="body-bold">Pet damage</Paragraph>
                  <Paragraph textStyle="body-sm" color="text.subtle">
                    Confidently rent to pet owners with coverage for things like carpets or fixtures.
                  </Paragraph>
                </Layout.Content>
                <Layout.Content css={{ gridColumnEnd: 'span 2', padding: '0', marginBottom: '200' }}>
                  <DetailedIconPaintRoller />
                </Layout.Content>
                <Layout.Content css={{ gridColumnEnd: 'span 10', padding: '0', marginBottom: '200' }}>
                  <Paragraph textStyle="body-bold">Repaint worn surfaces</Paragraph>
                  <Paragraph textStyle="body-sm" color="text.subtle">
                    Claim time and materials for repainting significant wear, beyond touch ups.
                  </Paragraph>
                </Layout.Content>
                <Layout.Content css={{ gridColumnEnd: 'span 2', padding: '0', marginBottom: '200' }}>
                  <Image src={ExcessCleaningIcon} alt="trash can" css={{ width: '44px', height: '44px' }} />
                </Layout.Content>
                <Layout.Content css={{ gridColumnEnd: 'span 10', padding: '0', marginBottom: '200' }}>
                  <Paragraph textStyle="body-bold">Excess cleaning</Paragraph>
                  <Paragraph textStyle="body-sm" color="text.subtle">
                    If your renter leaves behind an excessive mess, you may be reimbursed for clean up.
                  </Paragraph>
                </Layout.Content>
                <Layout.Content css={{ gridColumnEnd: 'span 2', padding: '0', marginBottom: '200' }}>
                  <Image src={EvictionCostsIcon} alt="scales of justice" css={{ width: '44px', height: '44px' }} />
                </Layout.Content>
                <Layout.Content css={{ gridColumnEnd: 'span 10', padding: '0', marginBottom: '200' }}>
                  <Paragraph textStyle="body-bold">Eviction costs</Paragraph>
                  <Paragraph textStyle="body-sm" color="text.subtle">
                    In the event of an eviction, proceeding costs and legal fees may be claimed.
                  </Paragraph>
                </Layout.Content>
              </Layout.Root>
            </Box>
          </Box>
        </div>
      }
    />
  );
};

export default RentalProtectionWhatsCoveredModal;
